<script setup>
  import { ref, onMounted, defineProps, defineEmits } from 'vue'
  import { ElMessage } from 'element-plus'
  import { parseTime } from '@/utils/index'
  import { subscriptionRecords, retrySubscriptionRecord } from '@/api/registrationSubscription'

  const props = defineProps({
    meetingCode: { type: String, required: true },
    subscription: { type: Object, required: true },
  })
  const emit = defineEmits(['close'])
  const rows = ref([])
  const total = ref(0)
  const page = ref(1)
  const loading = ref(false)
  const failed = ref(false)
  const retrying = ref(false)
  const labels = {
    PENDING: '待推送',
    SENDING: '推送中',
    RETRY: '等待重试',
    SUCCESS: '成功',
    FAILED: '失败',
    CANCELLED: '已取消',
  }
  async function load() {
    loading.value = true
    failed.value = false
    try {
      const { data } = await subscriptionRecords(props.meetingCode, props.subscription.id, page.value)
      rows.value = data.list || []
      total.value = data.total || 0
    } catch (error) {
      failed.value = true
    } finally {
      loading.value = false
    }
  }
  async function retry(row) {
    retrying.value = true
    try {
      await retrySubscriptionRecord(props.meetingCode, props.subscription.id, row.id)
      ElMessage.success('已加入重试队列')
      await load()
    } finally {
      retrying.value = false
    }
  }
  onMounted(load)
</script>

<template>
  <el-drawer
    custom-class="registration-delivery-records"
    :model-value="true"
    size="85%"
    :title="`${subscription.receiverName} · 推送记录`"
    @close="emit('close')"
  >
    <div class="records">
      <el-button :loading="loading" @click="load">刷新</el-button>
      <el-alert v-if="failed" :closable="false" title="推送记录加载失败，请重试" type="error" />
      <el-table v-loading="loading" border :data="rows" empty-text="暂无推送记录">
        <el-table-column label="事件ID" min-width="160" prop="eventId" show-overflow-tooltip />
        <el-table-column label="报名ID" min-width="100">
          <template #default="{ row }">{{ row.testEvent ? '模拟测试' : row.registrationId }}</template>
        </el-table-column>
        <el-table-column label="报名结果" width="100">
          <template #default="{ row }">
            {{ { APPROVED: '已通过', REJECTED: '已拒绝', CANCELLED: '已取消' }[row.status] || row.status }}
          </template>
        </el-table-column>
        <el-table-column label="投递状态" width="110">
          <template #default="{ row }">{{ labels[row.state] || row.state }}</template>
        </el-table-column>
        <el-table-column label="尝试次数" prop="attempts" width="90" />
        <el-table-column label="最近投递" min-width="170">
          <template #default="{ row }">{{ row.lastTime ? parseTime(row.lastTime) : '—' }}</template>
        </el-table-column>
        <el-table-column label="下次重试" min-width="170">
          <template #default="{ row }">
            {{ row.state === 'RETRY' && row.nextTime ? parseTime(row.nextTime) : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="失败原因" min-width="160" prop="lastError" show-overflow-tooltip />
        <el-table-column label="操作" width="80">
          <template #default="{ row }">
            <el-button
              v-if="row.state === 'FAILED' && subscription.enabled && !subscription.requiresSecretReset"
              :disabled="retrying"
              type="text"
              @click="retry(row)"
            >
              重试
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-model:current-page="page"
        layout="total, prev, pager, next"
        :page-size="20"
        :total="total"
        @current-change="load"
      />
    </div>
  </el-drawer>
</template>

<style scoped>
  .records {
    padding: 0 20px;
  }
  .el-table {
    margin: 16px 0;
  }
</style>

<style>
  .registration-delivery-records .el-drawer__body {
    min-height: 0;
    overflow: auto;
  }
</style>
